import { useMemo } from "react";
import { formatDateShort, formatNumber } from "../../../../utils/format";
import EmptyState from "../../../../components/EmptyState";

const WIDTH = 600;
const HEIGHT = 200;
const PADDING = { top: 20, right: 16, bottom: 28, left: 40 };

const ConversationVolumeChart = ({ data }) => {
  const points = useMemo(() => {
    if (!data || data.length === 0) return null;
    const maxY = Math.max(
      1,
      ...data.map((d) =>
        Math.max(Number(d.customerMessages || 0), Number(d.adminMessages || 0)),
      ),
    );
    const innerW = WIDTH - PADDING.left - PADDING.right;
    const innerH = HEIGHT - PADDING.top - PADDING.bottom;
    const stepX = data.length > 1 ? innerW / (data.length - 1) : innerW;
    const customer = data.map((d, i) => ({
      x: PADDING.left + i * stepX,
      y:
        PADDING.top +
        innerH -
        (Number(d.customerMessages || 0) / maxY) * innerH,
      v: Number(d.customerMessages || 0),
      day: d.day,
    }));
    const admin = data.map((d, i) => ({
      x: PADDING.left + i * stepX,
      y:
        PADDING.top +
        innerH -
        (Number(d.adminMessages || 0) / maxY) * innerH,
      v: Number(d.adminMessages || 0),
      day: d.day,
    }));
    return { customer, admin, maxY, innerH, stepX };
  }, [data]);

  if (!data || data.length === 0) {
    return <EmptyState description="Không có tin trong khoảng này" />;
  }

  const toPath = (pts) =>
    pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  const yTicks = [0, 0.5, 1];

  return (
    <div>
      <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 bg-blue-500" /> Tin khách
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-0.5 bg-emerald-500" /> Tin NV
        </span>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full h-auto"
        preserveAspectRatio="none"
      >
        {yTicks.map((t) => {
          const y = PADDING.top + points.innerH * (1 - t);
          const label = Math.round(points.maxY * t);
          return (
            <g key={t}>
              <line
                x1={PADDING.left}
                x2={WIDTH - PADDING.right}
                y1={y}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
              <text
                x={PADDING.left - 6}
                y={y + 3}
                textAnchor="end"
                fontSize="10"
                fill="#9ca3af"
              >
                {formatNumber(label)}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const x = PADDING.left + i * points.stepX;
          const show = data.length <= 10 || i === 0 || i === data.length - 1 || i % Math.ceil(data.length / 8) === 0;
          if (!show) return null;
          return (
            <text
              key={i}
              x={x}
              y={HEIGHT - 8}
              textAnchor="middle"
              fontSize="10"
              fill="#9ca3af"
            >
              {formatDateShort(d.day)}
            </text>
          );
        })}
        <path
          d={toPath(points.customer)}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
        />
        <path
          d={toPath(points.admin)}
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
        />
        {points.customer.map((p, i) => (
          <circle key={`c${i}`} cx={p.x} cy={p.y} r="2.5" fill="#3b82f6">
            <title>
              {formatDateShort(p.day)}: {formatNumber(p.v)} tin khách
            </title>
          </circle>
        ))}
        {points.admin.map((p, i) => (
          <circle key={`a${i}`} cx={p.x} cy={p.y} r="2.5" fill="#10b981">
            <title>
              {formatDateShort(p.day)}: {formatNumber(p.v)} tin NV
            </title>
          </circle>
        ))}
      </svg>
    </div>
  );
};

export default ConversationVolumeChart;
